import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Database, ExternalLink, Plus, Trash2 } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { formatDistanceToNow } from "date-fns";
import { de, enUS } from 'date-fns/locale';
import { Separator } from "@/components/ui/separator";

interface SupplementalFeed {
  id: string;
  supplemental_feed_id: string;
  display_name: string;
  status: string;
  last_upload_at: string | null;
  products_uploaded: number;
  created_at: string;
}

interface SupplementalFeedInlineProps {
  supplementalFeed: SupplementalFeed | null;
  merchantCenterId: string;
  loading: boolean;
  onCreateFeed: () => void;
  onDeleteFeed: () => void;
}

export const SupplementalFeedInline = ({
  supplementalFeed,
  merchantCenterId,
  loading,
  onCreateFeed,
  onDeleteFeed,
}: SupplementalFeedInlineProps) => {
  const { t, i18n } = useTranslation('products');
  if (!supplementalFeed) {
    return (
      <div className="pt-4 border-t">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">{t('supplementalFeed.title')}</span>
            <Badge variant="outline" className="text-xs">{t('supplementalFeed.notCreated')}</Badge>
          </div>
          <Button size="sm" onClick={onCreateFeed} disabled={loading}>
            {loading ? (
              <Spinner size="sm" />
            ) : (
              <>
                <Plus className="h-4 w-4 mr-1" />
                {t('supplementalFeed.create')}
              </>
            )}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          {t('supplementalFeed.createDesc')}
        </p>
      </div>
    );
  }

  const gmcUrl = `https://merchants.google.com/mc/products/datasources?merchantId=${merchantCenterId}`;

  return (
    <div className="pt-4 border-t space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">{t('supplementalFeed.title')}</span>
          <Badge variant={supplementalFeed.status === 'active' ? 'default' : 'secondary'} className="text-xs">
            {supplementalFeed.status}
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild>
            <a href={gmcUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3 w-3" />
            </a>
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onDeleteFeed}
            disabled={loading}
            className="h-8 w-8 p-0"
          >
            {loading ? (
              <Spinner size="sm" />
            ) : (
              <Trash2 className="h-3 w-3 text-destructive" />
            )}
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-3 text-xs">
        <div>
          <p className="text-muted-foreground">{t('supplementalFeed.productsLabel')}</p>
          <p className="font-semibold">{supplementalFeed.products_uploaded}</p>
        </div>
        <div className="col-span-2">
          <p className="text-muted-foreground">{t('supplementalFeed.lastUpload')}</p>
          <p className="font-semibold">
            {supplementalFeed.last_upload_at
              ? formatDistanceToNow(new Date(supplementalFeed.last_upload_at), { addSuffix: true, locale: i18n.language === 'de' ? de : enUS })
              : t('supplementalFeed.never')}
          </p>
        </div>
      </div>
      
      <div className="text-xs">
        <p className="text-muted-foreground mb-1">{t('supplementalFeed.feedId')}</p>
        <code className="text-xs bg-muted px-2 py-1 rounded block truncate">
          {supplementalFeed.supplemental_feed_id}
        </code>
      </div>
    </div>
  );
};
